/**
 * src/shell/portal/components/WorkspaceExtensionDrawer.tsx
 *
 * Token-Gated Enterprise Extensions Drawer for herx.org.
 * Decouples business apps from the consumer social portal:
 * Displays enterprise modules with credit unlock prices or license requirements.
 */

import React from 'react';
import { APP_CATALOG, AppId, AppEntitlement } from '../../../config/app-entitlements';
import { X, Lock, CheckCircle2, Sparkles, ExternalLink } from 'lucide-react';

export interface WorkspaceExtensionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  unlockedAppIds: Set<string>;
  availableCredits: number;
  onUnlockApp: (appId: AppId, price: number) => void;
  onLaunchApp: (appId: AppId) => void;
}

export const WorkspaceExtensionDrawer: React.FC<WorkspaceExtensionDrawerProps> = ({
  isOpen,
  onClose,
  unlockedAppIds,
  availableCredits,
  onUnlockApp,
  onLaunchApp,
}) => {
  if (!isOpen) return null;

  const catalogItems = Object.values(APP_CATALOG);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 10000,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 440,
          height: '100%',
          background: '#0c101a',
          borderLeft: '1px solid #1e293b',
          display: 'flex',
          flexDirection: 'column',
          color: '#f8fafc',
          boxShadow: '-10px 0 30px rgba(0,0,0,0.5)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontWeight: 800, fontSize: 17 }}>Workspace Extensions</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
              Enterprise &amp; business tools for sovereign operators
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Balance banner */}
        <div
          style={{
            padding: '12px 24px',
            background: '#070a10',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ fontSize: 12, color: '#94a3b8' }}>Available Karma/PoPR Credits:</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Sparkles size={14} /> {availableCredits.toLocaleString()} credits
          </span>
        </div>

        {/* Extensions List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {catalogItems.map((item: AppEntitlement) => {
            const isUnlocked = unlockedAppIds.has(item.id);
            const canAfford = availableCredits >= (item.tokenPricePerEpoch || 0);

            return (
              <div
                key={item.id}
                style={{
                  background: '#0f172a',
                  border: isUnlocked ? '1px solid #10b981' : '1px solid #1e293b',
                  borderRadius: 12,
                  padding: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#f8fafc' }}>
                    {item.name}
                  </div>
                  {isUnlocked ? (
                    <span
                      style={{
                        background: 'rgba(16, 185, 129, 0.1)',
                        border: '1px solid #10b981',
                        color: '#34d399',
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <CheckCircle2 size={12} /> Active
                    </span>
                  ) : (
                    <span
                      style={{
                        background: 'rgba(148, 163, 184, 0.1)',
                        border: '1px solid #334155',
                        color: '#94a3b8',
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <Lock size={12} /> Locked
                    </span>
                  )}
                </div>

                <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.4 }}>
                  {item.description}
                </div>

                <div
                  style={{
                    borderTop: '1px solid #1e293b',
                    paddingTop: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    {item.tokenPricePerEpoch ? `${item.tokenPricePerEpoch} credits / epoch` : 'Tier 4 License'}
                  </span>

                  {isUnlocked ? (
                    <button
                      type="button"
                      onClick={() => {
                        onLaunchApp(item.id);
                        onClose();
                      }}
                      style={{
                        background: '#10b981',
                        border: 'none',
                        borderRadius: 6,
                        color: '#ffffff',
                        padding: '6px 14px',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <ExternalLink size={13} /> Launch App
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onUnlockApp(item.id, item.tokenPricePerEpoch || 50)}
                      disabled={!canAfford}
                      style={{
                        background: canAfford ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : '#1e293b',
                        border: 'none',
                        borderRadius: 6,
                        color: canAfford ? '#ffffff' : '#64748b',
                        padding: '6px 14px',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: canAfford ? 'pointer' : 'not-allowed',
                      }}
                    >
                      Unlock with Tokens
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
